import { useEffect, useState } from "react";

import { useConnectToSocket } from "@/components/connect-to-socket";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";
import socketClient from "@/lib/socket-io";
import { ConcurrencyPayload } from "@classview/shared-types";

import { useCanUserJoin } from "./can-user-join";

type ConcurrencyStatus = {
  /**
   * Whether the socket client is connected to the server.
   */
  isConnected: boolean;
  /**
   * The number of participants in the call.
   */
  nbOfParticipants: number;
  /**
   * Whether the user can join the call. (allowedConcurrency < orgCount)
   */
  hasAvailableConcurrency: boolean;
};

export function useSubscribeToConcurrency(): ConcurrencyStatus {
  const [concurrencyStatus, setConcurrencyStatus] =
    useState<ConcurrencyPayload>({
      sessionId: "",
      sessionCount: 0,
      orgCount: 0,
      participants: [],
      allowedConcurrency: 0,
    });
  const isWebSocketAuthenticated = useVidyoStore(
    (state) => state.webSocket.isAuthenticated,
  );

  const { canJoinSession, session } = useCanUserJoin();
  const { isSocketConnected } = useConnectToSocket();
  const enabled =
    isSocketConnected && canJoinSession && isWebSocketAuthenticated;

  /**
   * This is used to handle the call status events of the socket client
   */
  useEffect(() => {
    if (!enabled || !session.data?.pin) {
      return;
    }

    const handleConcurrency = (data: ConcurrencyPayload) => {
      setConcurrencyStatus(data);
    };

    socketClient.on("concurrency", handleConcurrency);

    socketClient.emit("joinConcurrencyRoom", {
      sessionId: session.data.id,
      pin: Number(session.data.pin),
    });

    /**
     * The cleanup function is called when the component is unmounted
     * or when the dependencies change. (for example when the sessionId changes)
     */
    return () => {
      console.log("[useSubscribeToConcurrency] disconnecting");
      socketClient.off("concurrency", handleConcurrency);
    };
  }, [session.data?.id, session.data?.pin, enabled]);

  return {
    isConnected: isSocketConnected && concurrencyStatus.sessionId !== "",
    nbOfParticipants: concurrencyStatus.sessionCount,
    hasAvailableConcurrency:
      isSocketConnected &&
      concurrencyStatus.allowedConcurrency > concurrencyStatus.orgCount,
  };
}
